import { useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useStepper } from "../../../context/stepper-provider";
import { useNotification } from "../../../context/notification-provider";
import StepContentBox from "../components/shared/step-content-box";
import useFieldSelection from "../hooks/use-field-selection";
import FieldListItem from "../components/field/field-list-item";
import ShowAllToggle from "../components/entity/show-all-toggle";
import EmptyFieldState from "../components/field/empty-field-state";
import createFieldMenuActions from "../utils/field-menu-actions";
import { DEFAULT_DISPLAY_LIMIT } from "../constants";

const SelectFieldStep = () => {
  const { nextStep, completeStep, activeStep, isStepCompleted } = useStepper();
  const { showNotification } = useNotification();
  const hasAdvanced = useRef(false);
  const hasSelectedEntity = isStepCompleted(1);

  const {
    fields,
    displayedFields,
    showAll,
    isLoading,
    error,
    handleFieldToggle,
    handleClearSelection,
    handleShowAllToggle,
    refetch,
  } = useFieldSelection(hasSelectedEntity);

  const handleFieldSelect = (id: string) => {
    handleFieldToggle(id);

    // Auto-advance to next step
    if (!hasAdvanced.current) {
      hasAdvanced.current = true;
      requestAnimationFrame(() => {
        completeStep(activeStep);
        nextStep();
      });
    }
  };

  const handleClear = () => {
    handleClearSelection();
    hasAdvanced.current = false;
  };

  const handleRefresh = () => {
    refetch();
    showNotification({
      message: "Fields refreshed successfully",
      severity: "success",
    });
  };

  const menuActions = createFieldMenuActions({
    onRefresh: handleRefresh,
    onClearSelection: handleClear,
  });

  if (isLoading) {
    return (
      <StepContentBox
        title="FIELDS"
        menuActions={menuActions}
        contentPadding={0}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <CircularProgress />
        </Box>
      </StepContentBox>
    );
  }

  if (error) {
    return (
      <StepContentBox
        title="FIELDS"
        menuActions={menuActions}
        contentPadding={0}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",

            p: 4,
          }}
        >
          <Typography variant="body1" color="error">
            Error loading fields. Please try refreshing.
          </Typography>
        </Box>
      </StepContentBox>
    );
  }

  return (
    <StepContentBox title="FIELDS" menuActions={menuActions} contentPadding={0}>
      {hasSelectedEntity && fields.length > 0 ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              overflowY: showAll ? "auto" : "visible",
            }}
          >
            {displayedFields.map((field) => (
              <FieldListItem
                key={field.id}
                field={field}
                onToggle={handleFieldSelect}
              />
            ))}
          </Box>
          {fields.length > DEFAULT_DISPLAY_LIMIT && (
            <ShowAllToggle
              showAll={showAll}
              totalCount={fields.length}
              onToggle={handleShowAllToggle}
            />
          )}
        </Box>
      ) : (
        <EmptyFieldState
          hasSelectedEntity={hasSelectedEntity}
          hasFields={fields.length > 0}
        />
      )}
    </StepContentBox>
  );
};

export default SelectFieldStep;
